import React, { useState, useEffect, useRef } from 'react'
import { Drawer, Input, Button, List, Avatar, Spin, message, Empty, Tooltip } from 'antd'
import { SendIcon } from './Icons' // Ensure you have this icon or use another
import { internalChatsApi } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'

interface InternalChatDrawerProps {
    open: boolean
    onClose: () => void
    recipientId: number | null
    recipientName: string
    recipientRole: string
}

interface Message {
    id: number
    chat_id: number
    sender_id: number
    text_content: string
    is_read: boolean
    created_at: string
}

export const InternalChatDrawer: React.FC<InternalChatDrawerProps> = ({
    open,
    onClose,
    recipientId,
    recipientName,
    recipientRole
}) => {
    const { user } = useAuthStore()
    const [messages, setMessages] = useState<Message[]>([])
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)
    const [text, setText] = useState('')
    const [chatId, setChatId] = useState<number | null>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const pollingInterval = useRef<any | null>(null)

    // Scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        if (open && recipientId && user) {
            initializeChat()
        } else {
            // Clean up
            setMessages([])
            setChatId(null)
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current)
            }
        }

        return () => {
            if (pollingInterval.current) {
                clearInterval(pollingInterval.current)
            }
        }
    }, [open, recipientId, user])

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    const initializeChat = async () => {
        if (!recipientId) return
        setLoading(true)
        try {
            // 1. Get or create chat
            const chatRes = await internalChatsApi.startChat(recipientId)
            setChatId(chatRes.data.id)

            // 2. Load messages
            await loadMessages(chatRes.data.id)

            // 3. Start polling
            startPolling(chatRes.data.id)
        } catch (error) {
            console.error('Failed to init chat', error)
            message.error('Не удалось загрузить чат')
        } finally {
            setLoading(false)
        }
    }

    const loadMessages = async (id: number) => {
        try {
            const res = await internalChatsApi.getMessages(id, { limit: 100 })
            // API returns desc sorted, we need asc for display
            setMessages(res.data.reverse())
        } catch (error) {
            console.error('Failed to load messages', error)
        }
    }

    const startPolling = (id: number) => {
        if (pollingInterval.current) clearInterval(pollingInterval.current)
        pollingInterval.current = setInterval(() => {
            // Simple polling for now
            // Ideally should fetch only newer messages
            internalChatsApi.getMessages(id, { limit: 100 })
                .then(res => {
                    const newMessages = res.data.reverse()
                    setMessages(prev => {
                        if (newMessages.length !== prev.length || (newMessages.length > 0 && newMessages[newMessages.length - 1].id !== prev[prev.length - 1].id)) {
                            return newMessages
                        }
                        return prev
                    })
                })
                .catch(err => console.error('Polling error', err))
        }, 3000)
    }

    const handleSend = async () => {
        if (!text.trim() || !recipientId) return

        setSending(true)
        try {
            // Optimistic update? No, let's wait for server
            const res = await internalChatsApi.sendMessage({
                recipient_id: recipientId,
                text_content: text
            })

            const newMsg: Message = {
                ...res.data,
                created_at: new Date().toISOString() // Ensure date is present
            }

            setMessages(prev => [...prev, newMsg])
            setText('')
        } catch (error) {
            message.error('Ошибка отправки')
        } finally {
            setSending(false)
        }
    }

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        src={null} // We don't have avatar url in props currently, maybe add later
                        style={{ backgroundColor: '#1890ff' }}
                    >
                        {recipientName ? recipientName[0].toUpperCase() : 'U'}
                    </Avatar>
                    <div>
                        <div style={{ fontWeight: 600 }}>{recipientName}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 400 }}>
                            {recipientRole === 'teamlead' ? 'Тимлид' : 'Оператор'}
                        </div>
                    </div>
                </div>
            }
            placement="right"
            onClose={onClose}
            open={open}
            width={400}
            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
        >
            <div style={{ flex: 1, padding: 16, overflowY: 'auto', background: 'var(--bg-base)' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
                        <Spin />
                    </div>
                ) : messages.length === 0 ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Empty description="Нет сообщений" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {messages.map((msg) => {
                            const isMine = msg.sender_id === user?.id
                            return (
                                <div
                                    key={msg.id}
                                    style={{
                                        alignSelf: isMine ? 'flex-end' : 'flex-start',
                                        maxWidth: '80%',
                                    }}
                                >
                                    <div
                                        style={{
                                            padding: '8px 12px',
                                            borderRadius: 12,
                                            background: isMine ? '#1890ff' : 'var(--bg-card)',
                                            color: isMine ? '#fff' : 'var(--text-primary)',
                                            borderBottomRightRadius: isMine ? 2 : 12,
                                            borderBottomLeftRadius: isMine ? 12 : 2,
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {msg.text_content}
                                    </div>
                                    <div style={{
                                        fontSize: 10,
                                        color: 'var(--text-muted)',
                                        marginTop: 2,
                                        textAlign: isMine ? 'right' : 'left',
                                        paddingRight: isMine ? 4 : 0,
                                        paddingLeft: isMine ? 0 : 4
                                    }}>
                                        {format(new Date(msg.created_at), 'HH:mm', { locale: ru })}
                                        {isMine && (
                                            <span style={{ marginLeft: 4 }}>
                                                {msg.is_read ? '✓✓' : '✓'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            <div style={{
                padding: 16,
                borderTop: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                display: 'flex',
                gap: 8
            }}>
                <Input.TextArea
                    placeholder="Напишите сообщение..."
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            handleSend()
                        }
                    }}
                    style={{ borderRadius: 8 }}
                />
                <Button
                    type="primary"
                    icon={<SendIcon size={18} />}
                    onClick={handleSend}
                    loading={sending}
                    style={{
                        borderRadius: 8,
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                />
            </div>
        </Drawer>
    )
}
